/**
 * Home tab. Mini PRD: capture tile on Home + Care. Saved captures surface
 * here so the "document stored safely" leg of the loop is visible, and the
 * carer can delete any capture at any time (data-lifecycle constraint).
 */
import { Box, Stack, Typography, IconButton } from '@mui/material';

import { PageTemplate } from '@karehero/llama/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';
import { Card } from '@karehero/llama/components/atoms/Card/Card';
import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { useNavigation } from '@/hooks/useNavigationContext';
import CaptureTile from './components/CaptureTile';
import { XIcon, FileUpIcon } from './components/LocalIcons';
import { useCaptureFlow } from './CaptureFlowContext';
import { palette } from './components/palette';

export const HomeTab = () => {
  const navigate = useNavigation();
  const {
    savedCaptures,
    deleteCapture,
    setEntrySurface,
    setAutoOpenPicker,
    track,
  } = useCaptureFlow();

  return (
    <PageTemplate
      maxWidth='sm'
      headerSlot={
        <PageHeader
          title='Hello, Olivia'
          description="Here's where things stand with Margaret's care."
        />
      }
      contentSlot={
        <Stack spacing={2}>
          <CaptureTile surface='home' />

          {/* Benefits journey-triggered prompt (Mini PRD: benefits surfaces
              offer "upload your DWP letter") */}
          <Box
            sx={{
              backgroundColor: palette.lavender,
              borderRadius: 3,
              padding: 2.5,
            }}
          >
            <Stack sx={{ alignItems: 'flex-start' }} spacing={1}>
              <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
                <Box sx={{ color: palette.lavenderInk, display: 'flex' }}>
                  <FileUpIcon size={18} />
                </Box>
                <Typography sx={{ fontSize: 15 }}>
                  Heard back about Attendance Allowance?
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 14, color: palette.ink }}>
                Upload the DWP decision letter and Keni will tell you what it
                unlocks - for Margaret and for you.
              </Typography>
              <Button
                ariaLabel='Upload your DWP letter'
                variant='secondary'
                onPress={() => {
                  setEntrySurface('journey-prompt');
                  setAutoOpenPicker('file');
                  track('Capture Entry Tapped (proposed)', {
                    surface: 'journey-prompt',
                    journey: 'benefits',
                  });
                  navigate('/capture');
                }}
              >
                Upload your DWP letter
              </Button>
            </Stack>
          </Box>

          {savedCaptures.length > 0 && (
            <Stack spacing={1}>
              <Typography variant='h6'>Your documents</Typography>
              {savedCaptures.map((capture) => (
                <Card key={capture.id} isFullWidth isPaddingSm>
                  <Stack
                    direction='row'
                    spacing={1.5}
                    role='button'
                    aria-label={`Open ${capture.doc.classification}`}
                    onClick={() => navigate(`/documents/${capture.id}`)}
                    sx={{ alignItems: 'center', cursor: 'pointer' }}
                  >
                    {capture.doc.pageImages[0] ? (
                      <Box
                        component='img'
                        src={capture.doc.pageImages[0]}
                        alt=''
                        sx={{
                          width: 36,
                          height: 50,
                          objectFit: 'cover',
                          objectPosition: 'top',
                          borderRadius: 0.5,
                          border: `1px solid ${palette.divider}`,
                          backgroundColor: '#fff',
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Icon icon='file-lines' fontSize={20} />
                    )}
                    <Stack sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 14 }}>
                        {capture.doc.classification}
                      </Typography>
                      <Typography
                        sx={{ color: palette.mutedText, fontSize: 12 }}
                      >
                        {capture.doc.fileName} · Saved {capture.savedAt} ·{' '}
                        {capture.acceptedActions.length} action
                        {capture.acceptedActions.length === 1 ? '' : 's'} added
                      </Typography>
                    </Stack>
                    <IconButton
                      aria-label={`Delete ${capture.doc.fileName}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteCapture(capture.id);
                      }}
                      size='small'
                      sx={{ color: palette.mutedText }}
                    >
                      <XIcon size={16} />
                    </IconButton>
                  </Stack>
                </Card>
              ))}
              <Typography sx={{ color: palette.mutedText, fontSize: 11 }}>
                You can delete a document at any time. Deleting removes it and
                anything Keni suggested from it.
              </Typography>
            </Stack>
          )}
        </Stack>
      }
    />
  );
};

export default HomeTab;
